/**
 * Video Security Service
 * Handles security, rate limiting, and monitoring for video sessions
 */

import { db } from '../config/firebase';
import { collection, doc, setDoc, getDoc, updateDoc, serverTimestamp, query, where, getDocs, Timestamp } from 'firebase/firestore';

class VideoSecurityService {
  /**
   * Check if user can create a video room
   * @param {string} userId - User ID
   * @param {string} userType - User type
   * @returns {Promise<{allowed: boolean, reason?: string}>}
   */
  async canCreateRoom(userId, userType) {
    // Only admin and coach can create rooms
    if (userType !== 'admin' && userType !== 'coach') {
      return {
        allowed: false,
        reason: 'Only administrators and coaches can start video sessions. Students can only join existing sessions.'
      };
    }

    // Check rate limiting (max rooms per day)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStart = Timestamp.fromDate(today);

    try {
      const rateLimitQuery = query(
        collection(db, 'videoRoomCreations'),
        where('userId', '==', userId),
        where('createdAt', '>=', todayStart)
      );

      const snapshot = await getDocs(rateLimitQuery);
      const roomsCreatedToday = snapshot.size;

      const maxRoomsPerDay = 10; // Configurable limit
      if (roomsCreatedToday >= maxRoomsPerDay) {
        return {
          allowed: false,
          reason: `Rate limit exceeded. You can create up to ${maxRoomsPerDay} video sessions per day.`
        };
      }

      return { allowed: true };
    } catch (error) {
      console.error('Error checking room creation permission:', error);
      // Fail open for now, but log the error
      return { allowed: true };
    }
  }

  /**
   * Log room creation for security and monitoring
   * @param {string} userId - User ID
   * @param {string} userType - User type
   * @param {string} sessionId - Session ID
   * @param {string} roomName - Room name
   * @returns {Promise<void>}
   */
  async logRoomCreation(userId, userType, sessionId, roomName) {
    try {
      const logData = {
        userId,
        userType,
        sessionId,
        roomName,
        createdAt: serverTimestamp(),
        ipAddress: null, // Could be added if needed
        userAgent: navigator.userAgent
      };

      await setDoc(doc(db, 'videoRoomCreations', `${userId}_${Date.now()}`), logData);
    } catch (error) {
      console.error('Error logging room creation:', error);
      // Don't throw - logging failure shouldn't block room creation
    }
  }

  /**
   * Log room join for monitoring
   * @param {string} userId - User ID
   * @param {string} userType - User type
   * @param {string} sessionId - Session ID
   * @param {string} roomName - Room name
   * @returns {Promise<void>}
   */
  async logRoomJoin(userId, userType, sessionId, roomName) {
    try {
      const logData = {
        userId,
        userType,
        sessionId,
        roomName,
        action: 'join',
        timestamp: serverTimestamp(),
        userAgent: navigator.userAgent
      };

      await setDoc(doc(db, 'videoRoomJoins', `${userId}_${Date.now()}`), logData);
    } catch (error) {
      console.error('Error logging room join:', error);
    }
  }

  /**
   * Get active video sessions for a user
   * @param {string} userId - User ID
   * @returns {Promise<Array>}
   */
  async getActiveSessions(userId) {
    try {
      const q = query(
        collection(db, 'videoRoomCreations'),
        where('userId', '==', userId),
        where('createdAt', '>=', Timestamp.fromDate(new Date(Date.now() - 2 * 60 * 60 * 1000))) // Last 2 hours
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting active sessions:', error);
      return [];
    }
  }

  /**
   * Check if session duration limit is exceeded
   * @param {string} roomCreationId - Room creation document ID
   * @returns {Promise<boolean>}
   */
  async isSessionExpired(roomCreationId) {
    try {
      const docRef = doc(db, 'videoRoomCreations', roomCreationId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return true; // Consider expired if not found
      }

      const data = docSnap.data();
      const createdAt = data.createdAt?.toDate();
      if (!createdAt) {
        return false;
      }

      const maxDuration = 2 * 60 * 60 * 1000; // 2 hours
      const now = new Date();
      const elapsed = now - createdAt;

      return elapsed > maxDuration;
    } catch (error) {
      console.error('Error checking session expiration:', error);
      return false; // Fail closed
    }
  }
}

export default new VideoSecurityService();

